import { useCallback, useMemo } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  FlatSheetKey,
  inflatedTabsFamily,
  orderSheetProductLikeOverridesFamily,
} from "@/features/line-sheet-sets/helpers/sheet-state";
import useIdentity from "@/features/ui/hooks/use-identity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import {
  useAddOrderSheetProductLike,
  useDeleteOrderSheetProductLike,
} from "@/features/order-sheet-sets/hooks/use-toggle-order-sheet-product-like";

/**
 * 주문서에서 상품에 눌러 두는 '좋아요'.
 *
 * 서버 값은 상품(isLiked)에 실려 오고, 누른 직후의 상태만 덮어쓴다.
 * inflatedTabs 를 고치면 시트가 '수정됨'이 되어 저장 버튼이 열리므로 그쪽은 건드리지 않고,
 * 덮어쓴 값은 시트 단위 recoil 에 둔다 — 카드 보기와 자세히 보기가 같은 상태를 봐야 한다.
 */
export default function useOrderSheetProductLikes(
  sheetKey: FlatSheetKey,
  tabIndex: number
) {
  const identity = useIdentity();
  const inflatedTabs = useRecoilValue(inflatedTabsFamily(sheetKey));
  const [overrides, setOverrides] = useRecoilState(
    orderSheetProductLikeOverridesFamily(sheetKey)
  );
  const { error: showError } = useAppToasts();
  const { fire: fireAddLike } = useAddOrderSheetProductLike();
  const { fire: fireDeleteLike } = useDeleteOrderSheetProductLike();

  const serverLikedIds = useMemo(() => {
    const tab = inflatedTabs?.tabs[tabIndex];
    return new Set(
      (tab?.products ?? []).filter((p) => p.isLiked).map((p) => p.id)
    );
  }, [inflatedTabs, tabIndex]);

  const isLiked = useCallback(
    (productId: number) =>
      overrides[productId] ?? serverLikedIds.has(productId),
    [overrides, serverLikedIds]
  );

  const toggle = useCallback(
    (productId: number, onSettled?: () => void) => {
      const buyerId = identity?.company?.id;
      // 갤러리·그리드 모두 주문서에서만 쓰지만 sheetKey 타입은 라인시트도 겸한다.
      const orderSheetSetId =
        "orderSheetSetId" in sheetKey ? sheetKey.orderSheetSetId : -1;
      if (!buyerId || orderSheetSetId < 0) return;

      const nextLiked = !isLiked(productId);
      setOverrides((prev) => ({ ...prev, [productId]: nextLiked }));
      onSettled?.();

      const fire = nextLiked ? fireAddLike : fireDeleteLike;
      fire(
        { buyerId, orderSheetSetId },
        { lineSheetProductId: productId }
      ).catch((error) => {
        // 실패하면 눌린 채로 두지 않는다.
        setOverrides((prev) => ({ ...prev, [productId]: !nextLiked }));
        onSettled?.();
        showError(error);
      });
    },
    [
      identity,
      sheetKey,
      isLiked,
      setOverrides,
      fireAddLike,
      fireDeleteLike,
      showError,
    ]
  );

  return { isLiked, toggle };
}
